import { useEffect, useState, useCallback, MouseEvent } from 'react';
import moment, { Moment } from 'moment';
import { usePrevious } from 'src/hooks/usePrevious';
import { useAuth } from 'src/utils/auth';
import get from 'lodash/get';
import { DEFAULT_TIME_ZONE } from 'src/constants';
import { DEFAULT_DATE_FORMAT, TDatePickerProps } from '../types';

type TDatePickerState = {
    selectedDate: moment.Moment | undefined;
    selectedDateString: string;
};

export const useDatePickerState = (props: TDatePickerProps) => {
    const { onChange, name, value, format = DEFAULT_DATE_FORMAT, disabledDate } = props;
    const { user } = useAuth();
    const timeZone = get(user, 'profile.user.timezone', DEFAULT_TIME_ZONE);

    const [state, setState] = useState<TDatePickerState>({
        selectedDate: undefined,
        selectedDateString: '',
    });

    const updateSelectedDate = useCallback(
        (date: moment.Moment | null) => {
            if (date === null) {
                return {
                    selectedDate: undefined,
                    selectedDateString: '',
                };
            }
            return {
                selectedDate: date,
                selectedDateString: date.tz(timeZone).format(format),
            };
        },
        [format, timeZone]
    );

    const dateToMoment = (d?: Date | null) => {
        if (!d) {
            return null;
        }
        // this.setFirstDayOfWeek();
        const date = moment(d);
        return date.isValid() ? date : null;
    };

    const prevValue = usePrevious(value);
    useEffect(() => {
        if (value && prevValue !== value) {
            setState((prevState) => ({
                ...prevState,
                ...updateSelectedDate(dateToMoment(value)),
            }));
        }
    }, [format, prevValue, updateSelectedDate, value]);

    const clearDate = useCallback(() => {
        setState((prevState) => ({
            ...prevState,
            ...updateSelectedDate(null),
        }));
        onChange(null, name);
    }, [name, onChange, updateSelectedDate]);
    /**
     * Callback when the Clear button is clicked
     */
    const handleClear = (e: MouseEvent<unknown>) => {
        e.stopPropagation();
        clearDate();
    };

    const handleCheckIsDateDisabled = (d?: Moment | null) => {
        const date = d ? d.toDate() : undefined;
        return disabledDate ? disabledDate(date) : false;
    };

    /**
     * Called when date selected
     * @param date
     */
    const onDateSelect = useCallback(
        (date: moment.Moment | null) => {
            const nextDate = date;
            // if the back/forward button is clicked, rc-calendar selects the date
            // without checking if it is disabled. Check if the date is disabled, if it is, don't update anything
            const isDateDisabled =
                typeof disabledDate === 'function' ? disabledDate(date?.toDate()) : false;

            if (isDateDisabled) {
                return;
            }

            const newValue = updateSelectedDate(nextDate);
            setState((prevState) => ({
                ...prevState,
                ...newValue,
            }));

            // convert to JS date object
            const nextDateNative = nextDate ? nextDate.toDate() : null;
            onChange(nextDateNative, name);
        },
        [name, disabledDate, onChange, updateSelectedDate]
    );

    return {
        ...state,
        handleClear,
        handleCheckIsDateDisabled,
        onDateSelect,
        timeZone,
    };
};
